import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useMemo, useState } from 'react'
import { getLoanSubmissionAllDocumentHistory, getLoanSubmissionDocumentHistory } from 'services'
import { Modal, Pagination } from 'stories/components'
import { formatTime } from 'utils'

const documentHistoryTypeLabels: Record<string, string> = {
  upload: 'Upload',
  status: 'Status',
  category: 'Category',
  intext: 'Internal',
  borrower: 'Borrower',
}

const PAGE_SIZE = 20

/** childLevel 4 → content z-index 60, above Documents floater panel (z-50). */
const MODAL_CHILD_LEVEL = 4

export const DocumentHistoryModal = ({
  document,
  onClose,
}: {
  /** Pass a document for one-file history, or null for all documents on the loan. */
  document: any | null
  onClose: Function
}) => {
  const [isLoading, setLoading] = useState(true)
  const [history, setHistory] = useState<Record<string, any>[]>([])
  const [pageNum, setPageNum] = useState(0)
  const isAllDocuments = !document?.key

  useEffect(() => {
    const request = isAllDocuments
      ? getLoanSubmissionAllDocumentHistory()
      : getLoanSubmissionDocumentHistory(document.key)
    request
      .then((histories) => {
        setHistory(histories || [])
        setPageNum(0)
      })
      .finally(() => setLoading(false))
  }, [])

  const pageRows = useMemo(() => {
    const start = pageNum * PAGE_SIZE
    return history.slice(start, start + PAGE_SIZE)
  }, [history, pageNum])

  return (
    <Modal
      title={isAllDocuments ? 'Document History' : `History - ${document.name}`}
      titleOkay=""
      onClose={() => onClose()}
      isOpen={true}
      childLevel={MODAL_CHILD_LEVEL}
    >
      <div className="overflow-auto shadow-md sm:rounded-lg">
        <LayoutLoading show={isLoading} />
        <table className="w-full min-w-[680px] pl-6 text-left text-sm text-gray-900 dark:text-gray-400">
          <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-6 py-3">
                No
              </th>
              {isAllDocuments && (
                <th scope="col" className="px-6 py-3">
                  Document
                </th>
              )}
              <th scope="col" className="px-6 py-3">
                Type
              </th>
              <th scope="col" className="px-6 py-3">
                Value
              </th>
              <th scope="col" className="px-6 py-3">
                By
              </th>
              <th scope="col" className="px-6 py-3">
                Date
              </th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((item, index: number) => {
              const rowNo = pageNum * PAGE_SIZE + index + 1
              return (
                <tr key={`${item.documentKey || ''}-${item.createdAt}-${index}`} className={`${index % 2 ? 'bg-gray-50' : ''}`}>
                  <td className="px-6 py-3">{rowNo}</td>
                  {isAllDocuments && <td className="px-6 py-3">{item.documentName || item.documentKey || ''}</td>}
                  <td className="px-6 py-3">{documentHistoryTypeLabels[item.type] || item.type}</td>
                  <td className="px-6 py-3">{item.value}</td>
                  <td className="px-6 py-3">{item.createdBy}</td>
                  <td className="px-6 py-3">{formatTime(item.createdAt)}</td>
                </tr>
              )
            })}
            {!isLoading && history.length === 0 && (
              <tr>
                <td className="px-6 py-4 text-gray-500" colSpan={isAllDocuments ? 6 : 5}>
                  No document history found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {history.length > PAGE_SIZE && (
          <Pagination
            totalCount={history.length}
            itemCountPerPage={PAGE_SIZE}
            pageNum={pageNum}
            onNavigate={setPageNum}
          />
        )}
      </div>
    </Modal>
  )
}
